import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
  WritableSignal
} from '@angular/core';
import {FormGroupDirective} from '@angular/forms';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {berthInputFormatter, sendQuery} from '../../../../common/utils';
import {Berth, Mooring} from '@portbase/bezoekschip-service-typescriptmodels';
import {distinctUntilChanged, filter, map, Observable, switchMap} from 'rxjs';
import {VisitContext} from "../../../../visit-details/visit-context";

@Component({
  selector: 'app-operational-berth-editor',
  templateUrl: './operational-berth-editor.component.html',
  styleUrl: './operational-berth-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OperationalBerthEditorComponent implements OnInit {
  protected readonly form = inject(FormGroupDirective);
  private readonly destroyRef = inject(DestroyRef);

  protected selectedBerth: WritableSignal<Berth> = signal<Berth | null>(null);

  // Exposed for the template's search input
  portUnCode = input.required<string>();
  // Incoming = travelling to the first berth, outgoing = travelling to the exit point.
  // Neither incoming nor outgoing means a shifting trip (berth to berth).
  isIncomingTrip = input<boolean>(false);
  isOutgoingTrip = input<boolean>(false);
  // When true the caller is a pure LW (pilot) who may only edit the LW-allowed fields; MH-only controls
  // (Pilot Boarding Place dropdown, berth dropdown, bollards, mooring position) are hidden.
  isPilotOnly = input<boolean>(false);

  protected readonly berthInputFormatter = berthInputFormatter;

  ngOnInit(): void {
    const berthForm = this.form.control.get('berth');
    if (berthForm.get('code') && berthForm.get('name')) {
      this.selectedBerth.set({
        code: berthForm.get('code').value,
        name: berthForm.get('name').value,
      });
    }

    this.form.control.get('berth').get('code').valueChanges.pipe(
      distinctUntilChanged(),
      filter((code): code is string => !!code),
      switchMap(code =>
        this.findBerths(code).pipe(
          map((berths: Berth[]) => berths.find((b: Berth) => b.code === code) ?? {code, name: code})
        )
      ),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((berth: Berth) => {
      this.selectedBerth.set(berth);
      berthForm.get('name').setValue(berth?.name ?? null);
    });
  }

  searchBerth = (term: string) => this.findBerths(term);

  onBerthSelected(berth: Berth | null): void {
    this.selectedBerth.set(berth);
    this.form.control.get('berth').get('code').setValue(berth?.code ?? null);
    this.form.control.get('berth').get('code').markAsDirty();
    this.form.control.get('berth').get('name').setValue(berth?.name ?? null);
  }

  // Single query function — DRY
  private findBerths = (term: string): Observable<Berth[]> =>
    sendQuery('com.portbase.bezoekschip.common.api.visit.FindBerthsAndBuoys', {
      term,
      portUnCode: this.portUnCode(),
    });

  moorings: Mooring[] = [Mooring.PORT_SIDE, Mooring.STARBOARD_SIDE, Mooring.ALONGSIDE_OTHER_SHIP,
    Mooring.NO_PREFERENCE];
  mooringFormatter = (value: Mooring) => {
    switch (value) {
      case 'PORT_SIDE':
        return 'Port side';
      case 'STARBOARD_SIDE':
        return 'Starboard side';
      case 'ALONGSIDE_OTHER_SHIP':
        return 'Alongside other ship';
      case 'NO_PREFERENCE':
        return 'No preference';
      default:
        throw Error('Unknown mooring value: ' + value);
    }
  };

  protected readonly context = VisitContext;
}
